import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Send, Download, Search, Trash2, Inbox } from 'lucide-react';
import { toast } from 'sonner';
import { api, unwrap, downloadFile } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

type SubStatus = 'subscribed' | 'unsubscribed' | 'bounced';

interface Subscriber {
  id: string;
  email: string;
  status: SubStatus;
  source: string | null;
  subscribed_at: string | null;
  created_at: string;
}

interface Paginated<T> {
  data: T[];
  total: number;
  current_page: number;
  last_page: number;
}

const STATUS_STYLES: Record<SubStatus, { label: string; variant: 'default' | 'secondary' | 'success' | 'warning' | 'destructive' }> = {
  subscribed: { label: 'Suscrito', variant: 'success' },
  unsubscribed: { label: 'Baja', variant: 'secondary' },
  bounced: { label: 'Rebotado', variant: 'destructive' },
};

export const Route = createFileRoute('/_app/newsletter')({
  component: NewsletterPage,
});

function NewsletterPage() {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<SubStatus | ''>('');
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ['newsletter-subscribers', q, status, page],
    queryFn: () => {
      const params = new URLSearchParams({ per_page: '25', page: String(page) });
      if (q) params.set('q', q);
      if (status) params.set('status', status);
      return unwrap<Paginated<Subscriber>>(api.get(`/newsletter-subscribers?${params}`));
    },
  });

  const updateStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: SubStatus }) =>
      api.patch(`/newsletter-subscribers/${id}`, { status }),
    onSuccess: () => {
      toast.success('Suscriptor actualizado');
      void qc.invalidateQueries({ queryKey: ['newsletter-subscribers'] });
    },
    onError: () => toast.error('No se pudo actualizar'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/newsletter-subscribers/${id}`),
    onSuccess: () => {
      toast.success('Suscriptor eliminado');
      void qc.invalidateQueries({ queryKey: ['newsletter-subscribers'] });
    },
  });

  async function exportCsv() {
    try {
      await downloadFile('/newsletter-subscribers/export', 'newsletter-subscribers.csv');
    } catch {
      toast.error('No se pudo exportar el CSV');
    }
  }

  const syncAcelle = useMutation({
    mutationFn: async () => {
      const res = await api.post('/newsletter-subscribers/sync-acelle');
      return res.data as { message?: string };
    },
    onSuccess: (res) => toast.success(res.message ?? 'Sincronización con Acelle iniciada.'),
    onError: (err: unknown) => {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message
        ?? 'No se pudo sincronizar con Acelle.';
      toast.error(message);
    },
  });

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-6 md:p-8">
      <header className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-3xl font-bold tracking-tight">
            <Send className="size-7 text-neutral-700" /> Newsletter
          </h1>
          <p className="text-sm text-neutral-500">
            Suscriptores dados de alta desde el sitio.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => syncAcelle.mutate()} disabled={syncAcelle.isPending}>
            <Inbox className="size-4" /> {syncAcelle.isPending ? 'Sincronizando…' : 'Sincronizar con Acelle'}
          </Button>
          <Button variant="outline" onClick={exportCsv}>
            <Download className="size-4" /> Exportar CSV
          </Button>
        </div>
      </header>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-3">
            <div>
              <CardTitle>Suscriptores</CardTitle>
              <CardDescription>{data?.total ?? 0} suscriptores en total.</CardDescription>
            </div>
            <div className="flex gap-2">
              <select
                value={status}
                onChange={(e) => { setStatus(e.target.value as SubStatus | ''); setPage(1); }}
                className="h-9 rounded-md border border-neutral-200 bg-white px-3 text-sm"
              >
                <option value="">Todos los estados</option>
                {Object.entries(STATUS_STYLES).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
              <div className="relative w-64">
                <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-neutral-400" />
                <Input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Buscar email" className="pl-9" />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="border-y border-neutral-200 bg-neutral-50 text-left text-xs uppercase tracking-wider text-neutral-500">
              <tr>
                <th className="px-4 py-2">Estado</th>
                <th className="px-4 py-2">Email</th>
                <th className="px-4 py-2">Origen</th>
                <th className="px-4 py-2">Alta</th>
                <th className="px-4 py-2 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-neutral-400">Cargando…</td></tr>
              )}
              {data?.data.length === 0 && !isLoading && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-neutral-400">Aún no hay suscriptores.</td></tr>
              )}
              {data?.data.map((s) => {
                const st = STATUS_STYLES[s.status];
                return (
                  <tr key={s.id} className="border-b border-neutral-100 hover:bg-neutral-50">
                    <td className="px-4 py-3"><Badge variant={st.variant}>{st.label}</Badge></td>
                    <td className="px-4 py-3 font-medium">{s.email}</td>
                    <td className="px-4 py-3 text-neutral-500">{s.source ?? '—'}</td>
                    <td className="px-4 py-3 text-xs text-neutral-500">
                      {new Date(s.subscribed_at ?? s.created_at).toLocaleDateString('es')}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <select
                          value={s.status}
                          onChange={(e) => updateStatus.mutate({ id: s.id, status: e.target.value as SubStatus })}
                          className="h-8 rounded-md border border-neutral-200 bg-white px-2 text-xs"
                        >
                          {Object.entries(STATUS_STYLES).map(([k, v]) => (
                            <option key={k} value={k}>{v.label}</option>
                          ))}
                        </select>
                        <button
                          onClick={() => remove.mutate(s.id)}
                          className="rounded-md p-1.5 text-neutral-400 hover:bg-red-50 hover:text-red-600"
                          aria-label="Eliminar suscriptor"
                        >
                          <Trash2 className="size-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {data && data.last_page > 1 && (
            <div className="flex items-center justify-between border-t border-neutral-200 px-4 py-3 text-xs text-neutral-500">
              <span>Página {data.current_page} de {data.last_page}</span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)}>Anterior</Button>
                <Button size="sm" variant="outline" disabled={page >= data.last_page} onClick={() => setPage(page + 1)}>Siguiente</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
